import { Coding, QuestionnaireResponse, QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { CPTCodeQuestionsLinkIds } from "@/encounters/constants/cpt-code";
import { Extensions } from "@/fhir/enums/extensions";
import { joinReferenceString } from "@/utils/medplum";
import { ProceduresFormTypeWithId } from "@/zod-schemas/procedures";

type GetEncounterCPTCodeQuestionnaireResponseParams = {
  cptCode: ProceduresFormTypeWithId;
  status: QuestionnaireResponse["status"];
  patientId: string;
  encounterId: string;
  questionnaireId: string;
  questionnaireResponseId: string | null;
  versionId?: string;
};

export const getEncounterCPTCodeQuestionnaireResponse = ({
  cptCode,
  status,
  patientId,
  encounterId,
  questionnaireId,
  questionnaireResponseId,
  versionId,
}: GetEncounterCPTCodeQuestionnaireResponseParams) => {
  const questionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponseId ? { id: questionnaireResponseId } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", patientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    extension: [
      ...(versionId
        ? [{ url: Extensions.ParentQuestionnaireResponseVersionId, valueString: versionId }]
        : []),
    ],
    item: [
      {
        linkId: "cpt",
        item: [
          ...(cptCode.cptCode?.code
            ? [
                {
                  linkId: CPTCodeQuestionsLinkIds.Code,
                  text: "Procedure Code",
                  answer: [{ valueCoding: (cptCode.cptCode as Coding) ?? "" }],
                  extension: [
                    {
                      url: "http://quiltedhealth.com/sd/has-coding-input-details",
                      valueBoolean: true,
                    },
                  ],
                },
              ]
            : []),
          ...(cptCode.cptCodeNotes
            ? [
                {
                  linkId: CPTCodeQuestionsLinkIds.Notes,
                  text: "Procedure Code Notes",
                  answer: [{ valueString: cptCode.cptCodeNotes }],
                },
              ]
            : []),
          ...(cptCode.cptUnits
            ? [
                {
                  linkId: CPTCodeQuestionsLinkIds.Units,
                  text: "Units",
                  answer: [{ valueInteger: Number(cptCode.cptUnits) }],
                },
              ]
            : []),
          ...getDiagnosesFormsetItems(cptCode),
          ...getModifiersFormsetItems(cptCode),
        ],
      },
    ],
  } as QuestionnaireResponse;

  return questionnaireResponse;
};

const getDiagnosesFormsetItems = (cptCode: ProceduresFormTypeWithId) => {
  const diagnosesFormsetArray = Object.values(cptCode.cptDiagnosesFormset ?? {});

  if (!diagnosesFormsetArray.length) return [];

  const diagnosesFormsetItems = diagnosesFormsetArray.map((diagnosis) => {
    return {
      linkId: CPTCodeQuestionsLinkIds.DiagnosesFormset,
      text: "Diagnosis",
      item: [
        {
          linkId: CPTCodeQuestionsLinkIds.DiagnosesFormsetCode,
          extension: [
            {
              url: "http://hl7.org/fhir/StructureDefinition/questionnaire-hidden",
              valueBoolean: true,
            },
          ],
          answer: [{ valueString: diagnosis.cptDiagnosesFormsetCode }],
        },
        {
          linkId: CPTCodeQuestionsLinkIds.DiagnosesFormsetDiagnosis,
          extension: [
            {
              url: "http://quiltedhealth.com/sd/has-coding-input-details",
              valueBoolean: true,
            },
          ],
          answer: [{ valueCoding: diagnosis.cptDiagnosesFormsetDiagnosis as Coding }],
        },
        ...(diagnosis.cptDiagnosesFormsetAddToProblemList
          ? [
              {
                linkId: CPTCodeQuestionsLinkIds.DiagnosesFormsetAddToProblemList,
                text: "Add to Problem List",
                answer: [{ valueBoolean: diagnosis.cptDiagnosesFormsetAddToProblemList }],
              },
            ]
          : []),
      ],
    } as QuestionnaireResponseItem;
  });

  return diagnosesFormsetItems;
};

const getModifiersFormsetItems = (cptCode: ProceduresFormTypeWithId) => {
  const modifiersFormsetArray = Object.values(cptCode.cptModifiersFormset ?? {});

  if (!modifiersFormsetArray.length) return [];

  const modifiersFormsetItems = modifiersFormsetArray.map((modifier) => {
    return {
      linkId: CPTCodeQuestionsLinkIds.ModifiersFormset,
      text: "Modifier",
      item: [
        {
          linkId: CPTCodeQuestionsLinkIds.ModifiersFormsetCode,
          answer: [{ valueString: modifier.cptModifiersFormsetCode }],
        },
        {
          linkId: CPTCodeQuestionsLinkIds.ModifiersFormsetModifier,
          answer: [{ valueString: modifier.cptModifiersFormsetModifier }],
        },
      ],
    } as QuestionnaireResponseItem;
  });

  return modifiersFormsetItems;
};
